import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MODELS, downloadCsv } from '@/analytics/lib';
import { useTimezone } from '@/components/TimezoneContext';
import LeadsPage from '@/components/LeadsPage';
import VisitorsPage from '@/components/VisitorsPage';
import SalesPage from '@/components/SalesPage';
import StealthPage from '@/components/StealthPage';
import { DrawerHead, useDialogs } from './dialogs';
import { Icon, PlatformIcon, channelName } from './icons';
import { Avatar, Badge, Btn, EmptyState, Notice, PageHeading, SelectWrap, SettingsNote, money, num } from './ui';
import { API, mapJourney, modelLabel } from './data';

// Customer journeys — the prototype's journeys table, with Tether's other person-level directories (People,
// Visitors, Conversions, Registrations) as the table's tabs.
const STEP_ICON = { ad_click: null, landing: 'globe', embedded: 'code', checkout: 'bag', form: 'mail', conversion: 'bag' };
const WINDOWS = [{ value: 7, label: 'Last 7 days' }, { value: 30, label: 'Last 30 days' }, { value: 0, label: 'All time' }];
const TABS = [{ id: 'journeys', label: 'Journeys', path: '/journeys' }, { id: 'people', label: 'People', path: '/people' }, { id: 'visitors', label: 'Visitors', path: '/visitors' },
  { id: 'sales', label: 'Conversions', path: '/sales' }, { id: 'stealth', label: 'Registrations', path: '/stealth', funnel: 'webinar' }];

function stepIcon(step) {
  if (step.key === 'ad_click') return <PlatformIcon name={(step.meta || '').split(' / ')[0]} title={step.meta} />;
  return <span className="platform-icon direct" title={step.meta || step.label}><Icon name={STEP_ICON[step.key] || 'globe'} /></span>;
}

export default function CustomersPage({ tab = 'journeys', onSelectContact, shared = {}, me }) {
  const navigate = useNavigate();
  const tabs = TABS.filter(t => !t.funnel || me?.funnel_type === t.funnel);
  const active = tabs.some(t => t.id === tab) ? tab : 'journeys';
  return <>
    <PageHeading eyebrow="CUSTOMER JOURNEYS" title="Customer journeys" description="Connect the dots between discovery and conversion." />
    {active === 'journeys' ? <JourneysView onSelectContact={onSelectContact} tabs={tabs} active={active} onTab={id => navigate(tabs.find(t => t.id === id).path)} />
      : <section className="panel">
        <div className="table-toolbar"><TabStrip tabs={tabs} active={active} onTab={id => navigate(tabs.find(t => t.id === id).path)} /></div>
        {active === 'people' && <LeadsPage embedded {...shared} />}
        {active === 'visitors' && <VisitorsPage embedded {...shared} />}
        {active === 'sales' && <SalesPage embedded onSelectContact={id => onSelectContact?.(id, 'sales')} stats={shared.stats} />}
        {active === 'stealth' && <StealthPage embedded onSelectContact={onSelectContact} />}
      </section>}
  </>;
}

function TabStrip({ tabs, active, onTab, count }) {
  return <div className="table-tabs" role="group" aria-label="Customer views">{tabs.map(t => <button key={t.id} type="button" className={t.id === active ? 'selected' : ''} aria-pressed={t.id === active} onClick={() => onTab(t.id)}>{t.label}{t.id === active && count != null && <span className="count">{count}</span>}</button>)}</div>;
}

function JourneysView({ onSelectContact, tabs, active, onTab }) {
  const { openDrawer } = useDialogs();
  const { formatDate } = useTimezone();
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(7);
  const [channel, setChannel] = useState('');
  const [model, setModel] = useState('last');
  const q = useQuery({
    queryKey: ['journeys', search, days, 'all'],
    queryFn: () => { const p = new URLSearchParams({ limit: '50' }); if (search) p.set('search', search); if (days) p.set('days', String(days)); return fetch(`${API}/journeys?${p}`).then(r => { if (!r.ok) throw new Error('Failed to load journeys'); return r.json(); }); },
    refetchInterval: 20_000,
  });
  const all = useMemo(() => (q.data || []).map(mapJourney), [q.data]);
  const channels = useMemo(() => [...new Set(all.map(j => j.channel))].sort(), [all]);
  const rows = all.filter(j => !channel || j.channel === channel);
  const open = j => openDrawer({ label: `Journey of ${j.name || j.email || 'visitor'}`, body: <JourneyDrawer journey={j} model={model} onSelectContact={onSelectContact} /> });
  const exportJourneys = () => { downloadCsv(`tether-journeys.csv`, [{ key: 'name', label: 'Customer' }, { key: 'email', label: 'Email' }, { key: 'revenue', label: 'Lifetime revenue USD' }, { key: 'orders', label: 'Orders' }, { key: 'channel', label: 'First channel' }, { key: 'path', label: 'Journey', csv: j => j.steps.map(s => s.label).join(' → ') }], rows); toast(`Exported ${rows.length} journeys to CSV`); };
  return <>
    <div className="toolbar">
      <div className="toolbar-side">
        <SelectWrap label="Channel filter" icon="filter" value={channel} options={[{ value: '', label: 'All channels' }, ...channels.map(c => ({ value: c, label: c }))]} onChange={setChannel} />
        <SelectWrap label="Credit model" prefix="Credit:" value={model} options={MODELS} onChange={setModel} />
        <SelectWrap label="Journey window" icon="calendar" value={days} options={WINDOWS} onChange={v => setDays(Number(v))} />
        {(channel || search) && <Btn ghost onClick={() => { setChannel(''); setSearch(''); }}>Clear</Btn>}
      </div>
      <div className="toolbar-side"><span className="muted" style={{ fontSize: 12 }}>{q.isLoading ? 'Loading…' : `${num(rows.length)} journeys · ${WINDOWS.find(w => w.value === days)?.label.toLowerCase()}`}</span>
        <Btn icon="download" onClick={exportJourneys} disabled={!rows.length}>Export journeys</Btn></div>
    </div>
    <section className="panel">
      <div className="table-toolbar">
        <TabStrip tabs={tabs} active={active} onTab={onTab} count={rows.length} />
        <label className="search-input"><Icon name="search" /><input id="journey-search" aria-label="Search customers" placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} data-testid="journeys-search" /></label>
      </div>
      <div className="table-scroll">
        <table className="data-table">
          <thead><tr><th>Customer</th><th>Journey</th><th className="num">Lifetime revenue</th><th className="num">Orders</th><th className="num">Last activity</th><th /></tr></thead>
          <tbody>{q.isError ? <tr><td colSpan={6}><EmptyState icon="info" title="Could not load journeys" action={<Btn onClick={() => q.refetch()}>Try again</Btn>}>{q.error?.message}</EmptyState></td></tr>
            : q.isLoading ? <tr><td colSpan={6} className="muted">Loading journeys…</td></tr>
            : rows.length ? rows.map(j => (
              <tr key={`${j.contact_id}-${j.journey_no}`} onClick={() => open(j)} style={{ cursor: 'pointer' }} data-testid={`journey-card-${j.contact_id}`}>
                <td><div className="journey-profile"><Avatar name={j.name || j.email} /><span><button type="button" className="text-btn" onClick={e => { e.stopPropagation(); open(j); }}>{j.name || j.email || 'Anonymous visitor'}</button><small className="customer-id"><span className="type-ordinal">No.</span> {j.customerId}</small></span></div></td>
                <td><div className="journey-path">{j.steps.map((s, i) => <span key={i} className="row" style={{ gap: 6 }}>{i > 0 && <Icon name="right" />}{stepIcon(s)}</span>)}</div></td>
                <td className="num">{j.revenue ? money(j.revenue) : '—'}</td>
                <td className="num">{j.orders}</td>
                <td className="num">{formatDate(j.updated_at)}</td>
                <td><button type="button" className="btn ghost icon-btn" onClick={e => { e.stopPropagation(); open(j); }} aria-label={`View ${j.name || j.email || 'visitor'} journey`}><Icon name="arrowup" small /></button></td>
              </tr>))
            : <tr><td colSpan={6}><EmptyState icon="journey" title={search || channel ? 'No matching journeys' : days ? 'No journeys in this window' : 'Your first customer path starts here'} action={(search || channel || days) ? <Btn onClick={() => { setSearch(''); setChannel(''); setDays(0); }}>Clear filters</Btn> : undefined}>{search || channel || days ? 'Try all time or another search.' : 'Journeys appear when the tracking script identifies a visitor.'}</EmptyState></td></tr>}
          </tbody>
        </table>
      </div>
      <SettingsNote icon="shield">Identified people only. Lifetime revenue sums their recorded conversions; credit in the drawer follows the selected model.</SettingsNote>
    </section>
  </>;
}

// Credit per step for the drawer: tracked ad touches are eligible; the model splits the latest order among them.
function credits(steps, model) {
  const eligible = steps.map((s, i) => (s.key === 'ad_click' ? i : -1)).filter(i => i >= 0);
  const n = eligible.length;
  return steps.map((_, i) => {
    const pos = eligible.indexOf(i);
    if (pos < 0) return 0;
    if (n === 1) return 1;
    if (model === 'first') return pos === 0 ? 1 : 0;
    if (model === 'last') return pos === n - 1 ? 1 : 0;
    if (model === 'position') return n === 2 ? .5 : pos === 0 || pos === n - 1 ? .4 : .2 / (n - 2);
    return 1 / n;
  });
}

export function JourneyDrawer({ journey: j, model, onSelectContact }) {
  const { closeDialog } = useDialogs();
  const credit = credits(j.steps, model);
  const value = j.latestOrderRevenue || 0;
  return <>
    <DrawerHead label={<>CUSTOMER JOURNEY · <span className="type-id">{j.customerId}</span></>} closeLabel="Close customer journey">
      <div className="row" style={{ marginTop: 15 }}><Avatar name={j.name || j.email} size={42} /><div><h2 style={{ margin: 0 }}>{j.name || j.email || 'Anonymous visitor'}</h2><span className="muted type-id" style={{ fontSize: 12 }}>{j.email || ''}</span></div></div>
    </DrawerHead>
    <div className="drawer-body">
      <div className="drawer-metrics">
        <div><small>LIFETIME VALUE</small><strong>{j.revenue ? money(j.revenue) : '—'}</strong></div>
        <div><small>ORDERS</small><strong>{j.orders}</strong></div>
        <div><small>STEPS</small><strong>{j.steps.length}</strong></div>
      </div>
      <div className="between" style={{ marginBottom: 28 }}><h3>The path to purchase</h3><Badge tone="blue">{modelLabel(model)}</Badge></div>
      <div className="timeline">{j.steps.map((s, i) => (
        <div className="timeline-item" key={i}>{stepIcon(s)}<small>{s.chip ? s.chip.replace(/-/g, ' ') : ''}</small><h3>{s.label}{s.key === 'ad_click' ? ` · ${channelName((s.meta || '').split(' / ')[0])}` : ''}</h3><p>{s.meta}</p>
          {credit[i] ? <span className="timeline-credit">{(credit[i] * 100).toFixed(credit[i] === 1 || credit[i] === .5 ? 0 : 1)}% credit{value ? ` · ${money(value * credit[i], 2)}` : ''}</span>
            : <Badge style={{ marginTop: 8 }}>{s.key === 'conversion' ? 'Conversion event' : 'Supporting touch'} · No paid credit</Badge>}
          {s.key === 'conversion' && value ? <p style={{ marginTop: 7, color: 'var(--green)' }}>Order · {money(value, 2)}</p> : null}
        </div>))}</div>
      <Notice>Credit is applied to the latest order{value ? ` (${money(value, 2)})` : ''}. Only tracked ad touches receive attribution; landing pages, forms and checkouts are shown for context.</Notice>
      {onSelectContact && j.contact_id && <button type="button" className="btn" style={{ width: '100%', marginTop: 18 }} onClick={() => { closeDialog(); onSelectContact(j.contact_id); }}><Icon name="users" />Open full profile</button>}
    </div>
  </>;
}
